/**
 * Helpers to display a city with its French department number: "Ville (XX)".
 * DOM/TOM (971-978, 98x) keep 3 digits, mainland keeps 2 digits.
 */

const cleanDigits = (value?: string | null) => String(value ?? "").replace(/\D/g, "");

/** Returns "74", "33", "974"… or null when it cannot be determined. */
export function departmentFromPostalCode(postalCode?: string | null): string | null {
  const digits = cleanDigits(postalCode);
  if (digits.length < 2) return null;
  const two = digits.slice(0, 2);
  // Overseas: need 3 digits to be meaningful
  if (two === "97" || two === "98") {
    if (digits.length < 3) return null;
    return digits.slice(0, 3);
  }
  if (two === "20") return digits.length >= 3 ? (Number(digits.slice(0, 3)) < 202 ? "2A" : "2B") : "20";
  return two;
}

/**
 * Picks the department, preferring the backend-provided prefix when it is usable.
 */
export function resolveDepartment(
  postalCodePrefix?: string | null,
  postalCode?: string | null,
): string | null {
  const fromPrefix = departmentFromPostalCode(postalCodePrefix);
  if (fromPrefix) return fromPrefix;
  return departmentFromPostalCode(postalCode);
}

/** True when the city label already ends with a department suffix like "(33)". */
export function hasDepartmentSuffix(city?: string | null): boolean {
  return /\((?:2[AB]|\d{2,3})\)\s*$/.test(String(city ?? "").trim());
}

/** "Bègles" + "33" -> "Bègles (33)". Never duplicates an existing suffix. */
export function formatCityWithDepartment(
  city?: string | null,
  postalCodePrefix?: string | null,
  postalCode?: string | null,
): string {
  const label = String(city ?? "").trim();
  if (!label) return "";
  if (hasDepartmentSuffix(label)) return label;
  const dep = resolveDepartment(postalCodePrefix, postalCode);
  return dep ? `${label} (${dep})` : label;
}

/**
 * Adds the department inside a title that already mentions the city,
 * e.g. "Recherche de garde — Marie (Bègles)" -> "… (Bègles (33))" is avoided:
 * the city occurrence itself becomes "Bègles (33)".
 */
export function formatTitleWithDepartment(
  title: string,
  city?: string | null,
  postalCodePrefix?: string | null,
  postalCode?: string | null,
): string {
  const label = String(city ?? "").trim();
  if (!label || !title) return title;
  const decorated = formatCityWithDepartment(label, postalCodePrefix, postalCode);
  if (decorated === label) return title;
  if (title.includes(decorated)) return title;
  if (!title.includes(label)) return title;
  // Avoid nested parentheses when the title already wraps the city: "… (Bègles)"
  if (title.includes(`(${label})`)) return title.replace(`(${label})`, decorated);
  return title.replace(label, decorated);
}
